<script setup>
import { v4 as uuid } from 'uuid'
import { ref, watch, computed } from 'vue'

const props = defineProps({
  id: {
    type: String,
    default: () => `select-input-${uuid()}`
  },
  label: String,
  error: {
    type: [String, Array],
    default: null
  },
  modelValue: {
    type: [String, Number, null],
    default: null
  },
  options: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Select an option'
  }
})

const emit = defineEmits(['update:modelValue'])

const inputRef = ref(null)

defineExpose({
  focus: () => inputRef.value?.focus()
})

const serverError = ref(props.error)
const erroredValue = ref(props.modelValue)
const lastErrorMessage = ref(null)

const onChange = (e) => {
  const raw = e.target.value
  const value = raw === '' || raw === 'null' ? null : raw
  emit('update:modelValue', value)

  if (String(value) === String(erroredValue.value)) {
      serverError.value = lastErrorMessage.value
  } else {
      serverError.value = null
  }
}

// Watch for new errors from server
watch(() => props.error, (newError, oldError) => {
  const normalizeError = (err) => {
    if (!err) return null
    return Array.isArray(err) ? err[0] : err
  }
  
  const newErrorMsg = normalizeError(newError)
  const oldErrorMsg = normalizeError(oldError)
  
  if (newErrorMsg) {
    const isErrorChanged = newErrorMsg !== oldErrorMsg
    
    if (isErrorChanged || !lastErrorMessage.value) {
      serverError.value = newError
      erroredValue.value = props.modelValue
      lastErrorMessage.value = newErrorMsg
    }
  } else {
    serverError.value = null
    lastErrorMessage.value = null
  }
}, { immediate: true })

const displayError = computed(() => {
  if (!serverError.value) return null
  return Array.isArray(serverError.value) ? serverError.value[0] : serverError.value
})

const selectClasses = computed(() => [
  'w-full rounded-md border bg-white px-3 py-2 text-sm text-gray-900 transition-all duration-200',
  'focus:outline-none focus:ring-1',
  displayError.value
    ? 'border-red-500 focus:ring-red-300'
    : 'border-gray-300 hover:outline hover:outline-primary-400 focus:ring-primary-400'
])
</script>

<template>
  <div :class="$attrs.class">
    <label
      v-if="label"
      :for="id"
      class="block mb-1 text-sm font-semibold text-gray-600"
    >
      {{ label }}:
    </label>
    <select
      ref="inputRef"
      :id="id"
      v-bind="{ ...$attrs, class: null }"
      :value="modelValue ?? ''"
      @change="onChange"
      :class="selectClasses"
    >
      <option disabled value="">
        {{ placeholder }}
      </option>
      <option
        v-for="(option, index) in options"
        :key="index"
        :value="typeof option === 'object' ? (option.value ?? '') : option"
      >
        {{ typeof option === 'object' ? option.label : option }}
      </option>
    </select>
    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <span
        v-if="displayError"
        class="text-red-600 text-sm mt-1 block"
      >
        {{ displayError }}
      </span>
    </transition>
  </div>
</template>